import { all, take, takeEvery, put, call, select } from 'redux-saga/effects';
import { eventChannel } from 'redux-saga';
import { toSuccess, toError } from 'react-async-client';
import { path } from 'ramda';
import { message } from 'antd';
import { translate } from 'react-translate-component';

import { postSubscribeNotification, getMenuCounters, getUser } from 'actions/asyncActions';
import { LOGOUT, POST_SUBSCRIBE_NOTIFICATION, ALLOW_NOTIFICATION_PERMISSION, LOGIN, GET_USER, PASSWORD_CONFIRM } from 'constants/actionTypes';
import { pushServerNotification } from 'helpers/notificationHelper';
import { setNotificationPermission, setNotificationSubscribed } from 'actions/notificationActions';
import { isNotificationsSupported, firebaseMessaging, getFcmNewToken, deleteToken, getPermission, isNotificationError } from 'utils/firebase';

function* checkNotification(username, showError) {
    if (!isNotificationsSupported) {
        return false;
    }

    const isSubscribed = yield select(path(['notification', 'notificationSubscribed']));
    const currentNotificationPermission = yield select(path(['notification', 'notificationPermission']));
    const notificationActivated = yield select(path(['notification', 'notificationActivated']));

    try {
        let needSubscribe = !isSubscribed && (!currentNotificationPermission || !notificationActivated);

        if (!currentNotificationPermission && !showError) {
            throw new Error('No permission');
        }

        if (needSubscribe) {
            yield getPermission();
            const fcmToken = yield getFcmNewToken();

            yield put(setNotificationPermission(true));
            yield put(postSubscribeNotification({ username, fcmToken }));
        }
    } catch(error) {
        if (isNotificationError(error) && showError) {
            message.error(translate('notify.errors.permission'), 3);
        }

        yield put(setNotificationPermission(false));
    }
}

function getPushMessageChannel() {
    return isNotificationsSupported && eventChannel(emit => {
        const subscription = firebaseMessaging().onMessage(emit);
        return subscription;
    });
}

function* onMessageListener(msg) {
    pushServerNotification(msg.data);
    yield put(getMenuCounters({ isPushMessage: true }));
}

export default function* pushNotifications() {
    const pushChannel = yield call(getPushMessageChannel);

    yield all([
        pushChannel && takeEvery(pushChannel, onMessageListener),
        takeEvery([toSuccess(POST_SUBSCRIBE_NOTIFICATION)], function* () {
            yield put(setNotificationSubscribed(true));
        }),
        takeEvery([toError(POST_SUBSCRIBE_NOTIFICATION)], function* () {
            message.error(translate('errors.subscribeError'));
            yield put(setNotificationSubscribed(false));
        }),
        takeEvery([toSuccess(LOGIN), toSuccess(PASSWORD_CONFIRM)], function* takeUserData() {
            const action = yield take(toSuccess(GET_USER));
            const username = path(['payload', 'login'], action);
            yield checkNotification(username, false);
         }),
        takeEvery(ALLOW_NOTIFICATION_PERMISSION, function* (){
            const user = yield select(getUser.selectData);
            yield checkNotification(path(['login'], user), true);
        }),
        takeEvery([LOGOUT], function* () {
            if (isNotificationsSupported) {
                deleteToken();
                yield put(setNotificationSubscribed(false));
            }
        })
    ]);
}
